import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Button,
  IconButton,
  Typography,
  useTheme,
  Tooltip,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Key as KeyIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useSnackbar } from 'notistack';
import { User } from '../../types/user';
import { userService } from '../../services/userService';
import { UserForm } from './UserForm';
import { ConfirmDialog } from '../../components/ConfirmDialog/ConfirmDialog';

export const UserManagement: React.FC = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [userToToggle, setUserToToggle] = useState<User | null>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [actionType, setActionType] = useState<'toggle' | 'delete'>('toggle');
  const [searchTerm, setSearchTerm] = useState('');
  const [passwordResetDialog, setPasswordResetDialog] = useState<{
    open: boolean;
    user: User | null;
    newPassword: string;
    loading: boolean;
  }>({
    open: false,
    user: null,
    newPassword: '',
    loading: false
  });

  const fetchUsers = async () => {
    setLoading(true);
    try {
      console.log('🔍 DEBUG - Starting fetchUsers...');
      const data = await userService.getUsers();
      console.log('🔍 DEBUG - Fetched users:', data.slice(0, 3).map(u => ({ 
        id: u.id, 
        username: u.username, 
        employee_number: u.employee_number, 
        employeeNumber: u.employeeNumber 
      })));
      console.log('🔍 DEBUG - Total users fetched:', data.length);
      setUsers(data);
    } catch (error) {
      console.error('❌ DEBUG - Error fetching users:', error);
      enqueueSnackbar('Fehler beim Laden der Benutzer', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Filter users based on search term
  const filteredUsers = useMemo(() => {
    if (!searchTerm.trim()) {
      return users;
    }

    const searchLower = searchTerm.toLowerCase().trim();
    return users.filter(user => {
      const firstName = (user.first_name || user.firstName || '').toLowerCase();
      const lastName = (user.last_name || user.lastName || '').toLowerCase();
      const fullName = `${firstName} ${lastName}`.trim();
      const email = (user.email || '').toLowerCase();
      const username = (user.username || '').toLowerCase();

      return (
        firstName.includes(searchLower) ||
        lastName.includes(searchLower) ||
        fullName.includes(searchLower) ||
        email.includes(searchLower) ||
        username.includes(searchLower)
      );
    });
  }, [users, searchTerm]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const handleCreateUser = () => {
    setSelectedUser(null);
    setOpenForm(true);
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setOpenForm(true);
  };

  const handleToggleStatus = (user: User) => {
    setUserToToggle(user);
    setActionType('toggle');
    setOpenConfirmDialog(true);
  };

  const handleDeleteUser = (user: User) => {
    setUserToDelete(user);
    setActionType('delete');
    setOpenConfirmDialog(true);
  };

  const handleConfirmAction = async () => {
    try {
      if (actionType === 'toggle' && userToToggle) {
        const currentStatus = userToToggle.is_active !== undefined ? userToToggle.is_active : userToToggle.isActive;
        const newStatus = !currentStatus;
        await userService.toggleUserStatus(userToToggle.id, newStatus);
        enqueueSnackbar(
          `Benutzer wurde erfolgreich ${newStatus ? 'aktiviert' : 'deaktiviert'}`,
          { variant: 'success' }
        );
      } else if (actionType === 'delete' && userToDelete) {
        await userService.deleteUser(userToDelete.id);
        enqueueSnackbar('Benutzer wurde erfolgreich gelöscht', { variant: 'success' });
      }
      
      await fetchUsers();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.detail || 
        `Fehler beim ${actionType === 'toggle' ? 'Ändern des Status' : 'Löschen'} des Benutzers`;
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setOpenConfirmDialog(false);
      setUserToToggle(null);
      setUserToDelete(null);
    }
  };

  const handlePasswordReset = (user: User) => {
    setPasswordResetDialog({
      open: true,
      user,
      newPassword: '',
      loading: false
    });
  };

  const handlePasswordResetConfirm = async () => {
    if (!passwordResetDialog.user || !passwordResetDialog.newPassword.trim()) {
      enqueueSnackbar('Bitte geben Sie ein neues Passwort ein', { variant: 'error' });
      return;
    }

    if (passwordResetDialog.newPassword.length < 8) {
      enqueueSnackbar('Das Passwort muss mindestens 8 Zeichen lang sein', { variant: 'error' });
      return;
    }

    setPasswordResetDialog(prev => ({ ...prev, loading: true }));

    try {
      await userService.adminResetPassword(passwordResetDialog.user.id, passwordResetDialog.newPassword);
      enqueueSnackbar(`Passwort für ${passwordResetDialog.user.first_name} ${passwordResetDialog.user.last_name} wurde erfolgreich zurückgesetzt`, { variant: 'success' });
      setPasswordResetDialog({
        open: false,
        user: null,
        newPassword: '',
        loading: false
      });
    } catch (error: any) {
      console.error('Password reset error:', error);
      const errorMessage = error?.response?.data?.detail || 'Fehler beim Zurücksetzen des Passworts';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      setPasswordResetDialog(prev => ({ ...prev, loading: false }));
    }
  };

  const handlePasswordResetCancel = () => {
    setPasswordResetDialog({
      open: false,
      user: null,
      newPassword: '',
      loading: false
    });
  };

  const columns: GridColDef[] = [
    {
      field: 'username',
      headerName: 'Benutzername',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'employee_number',
      headerName: 'Mitarbeiternummer',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        if (!params || !params.row) return '';
        const empNum = params.row.employee_number || params.row.employeeNumber || 'Wird automatisch vergeben';
        if (params.row.id <= 33) { // Debug nur für die ersten paar User
          console.log('🔍 DEBUG - Employee number render:', {
            id: params.row.id,
            username: params.row.username,
            employee_number: params.row.employee_number,
            employeeNumber: params.row.employeeNumber,
            result: empNum
          });
        }
        return empNum;
      },
    },
    {
      field: 'first_name',
      headerName: 'Vorname',
      flex: 1,
      minWidth: 130,
      renderCell: (params) => {
        if (!params || !params.row) return '';
        return params.row.first_name || params.row.firstName || '';
      },
    },
    {
      field: 'last_name',
      headerName: 'Nachname',
      flex: 1,
      minWidth: 130,
      renderCell: (params) => {
        if (!params || !params.row) return '';
        return params.row.last_name || params.row.lastName || '';
      },
    },
    {
      field: 'email',
      headerName: 'E-Mail',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'phone',
      headerName: 'Telefon',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        if (!params || !params.row) return '';
        return params.row.phone || '';
      },
    },
    {
      field: 'role',
      headerName: 'Rolle',
      flex: 1,
      minWidth: 130,
      renderCell: (params) => {
        const roleLabels: Record<string, string> = {
          admin: 'Administrator',
          management: 'Management',
          foreman: 'Vorarbeiter',
          installer: 'Monteur',
        };
        return roleLabels[params.value] || params.value;
      },
    },
    {
      field: 'is_active',
      headerName: 'Status',
      flex: 1,
      minWidth: 130,
      renderCell: (params) => {
        if (!params || !params.row) return null;
        
        const isActive = params.row.is_active !== undefined ? params.row.is_active : params.row.isActive;
        
        return (
          <Chip
            label={isActive ? 'Aktiv' : 'Inaktiv'}
            color={isActive ? 'success' : 'default'}
            size="small"
          />
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Aktionen',
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => {
        if (!params || !params.row) return null;
        
        const user = params.row as User;
        const isActive = user.is_active !== undefined ? user.is_active : user.isActive;
        return (
          <Box>
            <Tooltip title="Bearbeiten">
              <IconButton
                size="small"
                onClick={() => handleEditUser(user)}
                sx={{ mr: 0.5 }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={isActive ? 'Deaktivieren' : 'Aktivieren'}>
              <IconButton
                size="small"
                onClick={() => handleToggleStatus(user)}
                sx={{ mr: 0.5 }}
              >
                {isActive ? <LockIcon /> : <LockOpenIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Passwort zurücksetzen">
              <IconButton
                size="small"
                onClick={() => handlePasswordReset(user)}
                sx={{ mr: 0.5 }}
              >
                <KeyIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Benutzer löschen">
              <IconButton
                size="small"
                onClick={() => handleDeleteUser(user)}
                color="error"
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  return (
    <Box sx={{ height: '100%', width: '100%', p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1">
          Benutzerverwaltung
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateUser}
        >
          Neuer Benutzer
        </Button>
      </Box>

      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <TextField
          placeholder="Nach Name, E-Mail oder Benutzername suchen..."
          value={searchTerm}
          onChange={handleSearchChange}
          variant="outlined"
          size="small"
          sx={{ 
            minWidth: 300,
            maxWidth: 500,
            flex: 1
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={handleClearSearch}
                  edge="end"
                  sx={{ mr: -0.5 }}
                >
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        {filteredUsers.length !== users.length && (
          <Typography variant="body2" color="text.secondary">
            {filteredUsers.length} von {users.length} Benutzern
          </Typography>
        )}
      </Box>

      <DataGrid
        rows={filteredUsers}
        columns={columns}
        loading={loading}
        autoHeight
        pageSizeOptions={[10, 25, 50]}
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
          sorting: { sortModel: [{ field: 'lastName', sort: 'asc' }] },
        }}
        sx={{
          '& .MuiDataGrid-cell': {
            borderColor: theme.palette.divider,
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: theme.palette.background.default,
            borderColor: theme.palette.divider,
          },
        }}
      />

      {openForm && (
        <UserForm
          open={openForm}
          onClose={() => setOpenForm(false)}
          user={selectedUser}
          onSuccess={() => {
            fetchUsers();
            setOpenForm(false);
          }}
        />
      )}

      <ConfirmDialog
        open={openConfirmDialog}
        onCancel={() => setOpenConfirmDialog(false)}
        onConfirm={handleConfirmAction}
        title={actionType === 'toggle' ? "Benutzerstatus ändern" : "Benutzer löschen"}
        content={
          actionType === 'toggle' && userToToggle
            ? `Möchten Sie den Benutzer "${userToToggle.first_name || userToToggle.firstName || ''} ${
                userToToggle.last_name || userToToggle.lastName || ''
              }" wirklich ${
                (userToToggle.is_active !== undefined ? userToToggle.is_active : userToToggle.isActive) 
                  ? 'deaktivieren' : 'aktivieren'
              }?`
            : actionType === 'delete' && userToDelete
            ? `Möchten Sie den Benutzer "${userToDelete.first_name || userToDelete.firstName || ''} ${
                userToDelete.last_name || userToDelete.lastName || ''
              }" wirklich dauerhaft löschen? Diese Aktion kann nicht rückgängig gemacht werden.`
            : ''
        }
      />

      {/* Passwort Reset Dialog */}
      <Dialog
        open={passwordResetDialog.open}
        onClose={handlePasswordResetCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Passwort zurücksetzen
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Neues Passwort für <strong>
              {passwordResetDialog.user?.first_name} {passwordResetDialog.user?.last_name}
            </strong> festlegen:
          </Typography>
          <TextField
            fullWidth
            type="password"
            label="Neues Passwort"
            value={passwordResetDialog.newPassword}
            onChange={(e) => setPasswordResetDialog(prev => ({ 
              ...prev, 
              newPassword: e.target.value 
            }))}
            helperText="Mindestens 8 Zeichen"
            disabled={passwordResetDialog.loading}
            autoFocus
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handlePasswordResetCancel}
            disabled={passwordResetDialog.loading}
          >
            Abbrechen
          </Button>
          <Button
            onClick={handlePasswordResetConfirm}
            variant="contained"
            color="primary"
            disabled={passwordResetDialog.loading || !passwordResetDialog.newPassword.trim()}
          >
            {passwordResetDialog.loading ? 'Wird gesetzt...' : 'Passwort setzen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}; 